import {objectFromEntries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import _ from 'underscore';

import * as Analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import {
  ActionOrigin,
  ANALYTICS_MODULE,
  AnalyticsMetadataForContext,
  COMMON_ANALYTICS_ACTIONS,
  ModalMetadataEntryType,
} from 'constants/AnalyticsConstants';
import ModelTypeConstants, {StorableItemType, PARENT_STORABLE_ITEMS} from 'constants/ModelTypeConstants';
import {
  GridCellSummary,
  SelectedParentStorable,
  STORABLE_CLASS_TO_VALID_PARENT_CLASSES,
} from 'constants/SampleConstants';
import {SampleStatusItem} from 'constants/SampleStatusConstants';
import * as Context from 'Context';
import {
  canProceedWithSamples,
  checkForSampleStatusWarnings,
  SampleStatusWarning,
} from 'inventory/sample-status/SampleStatusWarning';
import {BaseSampleStatusWarningTrackingParams} from 'inventory/sample-status/SampleStatusWarningTracking';
import {BaseParentStorableDataSelector} from 'samples/ParentStorableDataSelector';
import * as SampleUtil from 'samples/SampleUtil';
import * as StorableActions from 'samples/StorableActions';
import {BulkMoveStorableData} from 'samples/StorableDAO';
import * as ParentStorableUtil from 'samples/storables/ParentStorableUtil';
import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';
import {ButtonStyle} from 'ui-library/buttons/Button';
import HorizontalChipListField from 'ui-library/chips/HorizontalChipListField';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {RadioButtonSelector} from 'ui-library/inputs/RadioButtonGroup';
import * as ModalContainer from 'ui-library/ModalContainer';
import Modal from 'ui-library/modals/Modal';
import APIUtil from 'util/APIUtil';

import {StorableSummaryGraphQL} from '../../graphql-fragments/inventory/__generated/StorableSummaryGraphQL';

function closeMoveStorablesModal(): void {
  ModalContainer.close(MoveStorablesModal);
}

export function openMoveStorablesModal(props: Omit<MoveStorablesModalProps, 'onRequestClose'>): void {
  ModalContainer.open(
    MoveStorablesModal,
    <MoveStorablesModal {...props} onRequestClose={closeMoveStorablesModal} />
  );
}

export const NATIVE_INPUT_MODE: unique symbol = Symbol('MoveStorablesModal-native-input-type');
type InputMode = typeof NATIVE_INPUT_MODE | string; // string to support any custom input modes

// Note: can be null if the ENABLE_REQUIRED_PARENT_STORABLE config flag is on, or if the storable being moved
// is a location (locations can be root nodes of the inventory tree)
export type ParentStorableId = BulkMoveStorableData['parentStorableData']['id'];

export interface CustomParentStorableInput {
  inputMode: string;
  inputLabel: string;
  // Currently only supports location parents. Supporting grid parents would require tracking the
  // coordinates as well.
  validParentIdByStorableId: Map<string, ParentStorableId>;
  getBlockingErrors: (selectedSampleIds: Array<string>) => Array<string>;
  renderCustomInput: (selectedSampleIds: Array<string>) => JSX.Element;
  analyticsMetadataEntryType: ModalMetadataEntryType;
}

export interface MoveStorablesModalProps {
  storables: Array<StorableSummaryGraphQL>;
  customParentStorableInputs?: Array<CustomParentStorableInput>;
  preselectedParentStorable?: StorableSummaryGraphQL;
  onRequestClose: () => void;
  initialInputMode?: InputMode;
  analyticsMetadata?: AnalyticsMetadataForContext;
  shouldAlwaysShowSampleStatusTable?: boolean;
  onSuccess?: () => void;
}

interface State {
  canProceedOnSampleStatus: boolean;
  nativeInputSelectedParentStorable: SelectedParentStorable;
  selectedSampleIds: Array<string>;
  selectedStorables: Array<StorableSummaryGraphQL>;
  selectedInputMode: InputMode;
  shouldShowStatusWarnings: boolean;
  samples: Array<SampleStatusItem> | null;
  loading: boolean;
  isSubmitting: boolean;
  submitErrorMessage: string | null;
  /** Mapping from storable id to storable. Leveraged to maintain correct ordering of selectedStorables. */
  storablesById: Record<string, StorableSummaryGraphQL> | null;
  /** Tooltips for individual grid cells to show which container is being moved where. Keyed by
   * position, in the format A1. */
  gridCellTooltipTextByPosition: {[gridPosition: string]: string};
}

export default class MoveStorablesModal extends React.Component<MoveStorablesModalProps> {
  state: State = {
    canProceedOnSampleStatus: false,
    nativeInputSelectedParentStorable: ParentStorableUtil.getEmptySelectedParent(),
    selectedSampleIds: [],
    selectedStorables: [],
    storablesById: null,
    gridCellTooltipTextByPosition: {},
    selectedInputMode: this.props.initialInputMode ?? NATIVE_INPUT_MODE,
    shouldShowStatusWarnings: false,
    samples: null,
    // Initialized to true because load (checkForWarnings) is called immediately in componentDidMount
    loading: true,
    isSubmitting: false,
    submitErrorMessage: null,
  };
  analyticsMetadata = {
    ...{actionOrigin: ActionOrigin.UI_UNDETERMINED},
    ...(this.props.analyticsMetadata ?? {}),
    modalType: 'move_storables',
  };
  sampleStatusWarningTrackingParams: BaseSampleStatusWarningTrackingParams = {
    actionAttempted: 'move_location',
    actionOrigin: this.props.analyticsMetadata?.actionOrigin ?? ActionOrigin.UI_UNDETERMINED,
    eventGroupId: this.props.analyticsMetadata?.eventGroupId ?? Analytics.makeAnalyticsEventId(),
  };

  private async checkForWarnings(): Promise<void> {
    const sampleIds = this.props.storables.map(({id}) => id);
    const {shouldShowWarning, samples} = await checkForSampleStatusWarnings({
      sampleIds,
      shouldAlwaysShowWarning: this.props.shouldAlwaysShowSampleStatusTable,
    });
    this.setState({shouldShowStatusWarnings: shouldShowWarning, samples: samples ?? null});
    this.setSelectedSampleIds(sampleIds);
    this.setState({loading: false});
  }

  private setCanProceedOnSampleStatus = (override: boolean): void => {
    this.setState({canProceedOnSampleStatus: override});
  };

  private setSelectedSampleIds = (sampleIds: Array<string>): void => {
    // Deselecting & re-selecting items can change their order. We need to ensure that the order of
    // selectedStorables is the same as selectedSampleIds so that what's rendered matches which container is
    // actually being moved where in a grid.
    const storablesById =
      this.state.storablesById ??
      objectFromEntries(this.props.storables.map((storable) => [storable.id, storable]));
    const selectedStorables = sampleIds.map((sampleId) => storablesById[sampleId]);
    this.setState({
      selectedSampleIds: sampleIds,
      selectedStorables,
      ...(this.state.storablesById == null && {storablesById}),
    });
    this.updateGridCellTooltips(this.state.nativeInputSelectedParentStorable.gridCells, selectedStorables);
  };

  private updateGridCellTooltips = (
    gridCells: Array<GridCellSummary>,
    selectedStorables: Array<StorableSummaryGraphQL>
  ): void => {
    if (!gridCells) {
      this.setState({gridCellTooltipTextByPosition: {}});
      return;
    }
    const gridCellTooltipTextByPosition = gridCells.reduce(
      (tooltipByPosition: {[gridPosition: string]: string | null}, gridCell, index) => {
        if (index < selectedStorables.length) {
          const storableAtPosition = selectedStorables[index];
          const positionStr = SampleUtil.formatGridCoordinates(gridCell.rowIndex, gridCell.columnIndex);
          const tooltip = `${storableAtPosition.name}${
            storableAtPosition.barcode?.value !== storableAtPosition.name
              ? ` (${storableAtPosition.barcode?.value})`
              : ''
          }`;
          tooltipByPosition[positionStr] = tooltip === '' ? null : tooltip;
        }
        return tooltipByPosition;
      },
      {}
    );
    this.setState({gridCellTooltipTextByPosition});
  };

  private setNativeInputSelectedParent = (newSelectedParent: SelectedParentStorable): void => {
    this.updateGridCellTooltips(newSelectedParent.gridCells, this.state.selectedStorables);
    this.setState({nativeInputSelectedParentStorable: newSelectedParent});
  };

  private resetNativeInputSelectedParent = (): void => {
    const emptyParent = ParentStorableUtil.getEmptySelectedParent();
    this.updateGridCellTooltips(emptyParent.gridCells, this.state.selectedStorables);
    this.setState({nativeInputSelectedParentStorable: emptyParent});
  };

  private setSelectedInputMode = (newInputMode: InputMode): void => {
    this.setState({selectedInputMode: newInputMode});
  };

  componentDidMount(): void {
    this.setState({
      storablesById: objectFromEntries(this.props.storables.map((storable) => [storable.id, storable])),
    });
    this.checkForWarnings();
    if (this.props.preselectedParentStorable) {
      this.setState({
        nativeInputSelectedParentStorable: {storable: this.props.preselectedParentStorable, gridCells: []},
      });
    }
    Analytics.trackEventWithModule(
      COMMON_ANALYTICS_ACTIONS.OPENED_MODAL,
      ANALYTICS_MODULE.SAMPLES,
      this.analyticsMetadata
    );
  }

  componentDidUpdate(prevProps: MoveStorablesModalProps, prevState: State): void {
    if (prevState.isSubmitting && !this.state.isSubmitting && !this.state.submitErrorMessage) {
      prevProps.onRequestClose();
    }
  }

  private getCustomParentStorableInput(customInputMode: InputMode): CustomParentStorableInput {
    const foundConfig = this.props.customParentStorableInputs?.find(
      ({inputMode}) => inputMode === customInputMode
    );
    assert(foundConfig != null, 'expected custom parent storable input config to be present');
    return foundConfig;
  }

  private getBulkMoveStorableDatasForSubmitFromNativeInput = (): Array<BulkMoveStorableData> => {
    const parentStorableDatas = ParentStorableUtil.getParentStorableDatas(
      this.state.selectedSampleIds.length,
      this.state.nativeInputSelectedParentStorable
    );
    return _.zip(this.state.selectedSampleIds, parentStorableDatas).map(
      ([storableId, parentStorableData]) => ({
        id: storableId,
        parentStorableData,
      })
    );
  };

  private getBulkMoveStorableDatasForSubmitFromCustomInput = (): Array<BulkMoveStorableData> => {
    const customInputValidParentIdByStorableId = this.getCustomParentStorableInput(
      this.state.selectedInputMode
    ).validParentIdByStorableId;

    const storableDatas = this.state.selectedSampleIds
      .filter((id) => customInputValidParentIdByStorableId.has(id))
      .map((id) => ({
        id,
        parentStorableData: {
          id: customInputValidParentIdByStorableId.get(id)!,
          gridCellColumnIndex: null,
          gridCellRowIndex: null,
        },
      }));
    return storableDatas;
  };

  private getBulkMoveStorableDatasForSubmit = (): Array<BulkMoveStorableData> =>
    this.state.selectedInputMode === NATIVE_INPUT_MODE
      ? this.getBulkMoveStorableDatasForSubmitFromNativeInput()
      : this.getBulkMoveStorableDatasForSubmitFromCustomInput();

  private handleCompleteSubmitAttempt = (errorMessage?: string): void => {
    this.setState({isSubmitting: false, submitErrorMessage: errorMessage ?? null});
  };

  // Not private because tests use it :-\
  handleSubmit = (): void => {
    const moveDatas = this.getBulkMoveStorableDatasForSubmit();
    this.setState({isSubmitting: true, submitErrorMessage: null});
    Context.usingAsync({analyticsMetadata: this.analyticsMetadata}, null, () => {
      StorableActions.bulkMoveStorablesWithDataMods(
        moveDatas,
        this.props.onSuccess,
        this.handleCompleteSubmitAttempt
      );
    });
    const analyticsMetadataForSubmit = {
      ...this.analyticsMetadata,
      ...(this.props.customParentStorableInputs && {
        modalMetadataEntryInfo: [
          {
            entryType:
              this.state.selectedInputMode === NATIVE_INPUT_MODE
                ? ModalMetadataEntryType.MANUAL
                : this.getCustomParentStorableInput(this.state.selectedInputMode).analyticsMetadataEntryType,
            metadataElement: 'location',
          },
        ],
      }),
    };
    Analytics.trackEventWithModule(
      COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL,
      ANALYTICS_MODULE.SAMPLES,
      analyticsMetadataForSubmit
    );
  };

  private handleClose = (): void => {
    Analytics.trackEventWithModule(
      COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL,
      ANALYTICS_MODULE.SAMPLES,
      this.analyticsMetadata
    );
    this.props.onRequestClose();
  };

  private getInvalidGridPositionsInNativeInputErrorMessage = (
    parentStorable: StorableSummaryGraphQL
  ): string | null => {
    // If we're using BaseParentStorableDataSelector, number of selected grid cells must match number of
    // storables being moved
    if (SampleUtil.isGrid(parentStorable.id)) {
      const numStorables = this.state.selectedSampleIds.length;
      const numGridCellsSelected = this.state.nativeInputSelectedParentStorable.gridCells.length;
      if (numGridCellsSelected !== numStorables) {
        return `Please select exactly ${numStorables} ${pluralize(
          numStorables,
          'position'
        )} (${numGridCellsSelected} currently selected).`;
      }
    }
    return null;
  };

  private getInvalidParentStorableTypeInNativeInputErrorMessage = (
    parentStorable: StorableSummaryGraphQL
  ): string | null => {
    const storablesWithInvalidParents = [];
    const parentStorableType = APIUtil.apiIdToType(parentStorable.id);
    for (const storable of this.state.selectedStorables) {
      const storableType = APIUtil.apiIdToType(storable.id);
      if (
        !STORABLE_CLASS_TO_VALID_PARENT_CLASSES[storableType].includes(
          parentStorableType as unknown as StorableItemType
        )
      ) {
        storablesWithInvalidParents.push(storable);
      }
    }
    return storablesWithInvalidParents.length !== 0
      ? `Invalid location for ${storablesWithInvalidParents.map((storable) => storable.name).join(', ')}.`
      : null;
  };

  private getNativeInputValidationErrorMessage = (): string | null => {
    const selectedParentStorableSummary = this.state.nativeInputSelectedParentStorable.storable;
    if (selectedParentStorableSummary == null) {
      return null;
    }
    const invalidParentStorableError = this.getInvalidParentStorableTypeInNativeInputErrorMessage(
      selectedParentStorableSummary
    );
    if (invalidParentStorableError) {
      return invalidParentStorableError;
    }
    return this.getInvalidGridPositionsInNativeInputErrorMessage(selectedParentStorableSummary);
  };

  private getValidationErrorMessages(): Array<string> {
    if (this.state.selectedInputMode === NATIVE_INPUT_MODE) {
      const errorMessage = this.getNativeInputValidationErrorMessage();
      return errorMessage ? [errorMessage] : [];
    }
    return this.getCustomParentStorableInput(this.state.selectedInputMode).getBlockingErrors(
      this.state.selectedSampleIds
    );
  }

  private isMoveButtonDisabled(
    canProceedBasedOnSampleState: boolean,
    validationErrors: Array<string>
  ): boolean {
    // ParentStorable must be selected when using the native input mode
    const isParentStorableMissing =
      this.state.selectedInputMode === NATIVE_INPUT_MODE &&
      this.state.nativeInputSelectedParentStorable.storable == null;

    return (
      this.state.isSubmitting ||
      Boolean(validationErrors.length) ||
      this.getBulkMoveStorableDatasForSubmit().length === 0 ||
      !canProceedBasedOnSampleState ||
      isParentStorableMissing
    );
  }

  render(): JSX.Element {
    const customParentStorableInputs = this.props.customParentStorableInputs ?? [];
    const selectedStorablesIncludeNonContainers = this.state.selectedStorables.some(
      ({id}) => !APIUtil.isContainer(id)
    );

    const possibleWarning = this.state.shouldShowStatusWarnings && this.state.samples != null && (
      <SampleStatusWarning
        sampleIds={this.props.storables.map(({id}) => id)}
        selectedSampleIds={this.state.selectedSampleIds}
        setSelectedSampleIds={this.setSelectedSampleIds}
        shouldProceed={this.state.canProceedOnSampleStatus}
        setShouldProceed={this.setCanProceedOnSampleStatus}
        trackingParams={this.sampleStatusWarningTrackingParams}
        samples={this.state.samples}
      />
    );
    const radioOptions: Array<{label: string; value: InputMode}> = [
      {label: 'Search for locations', value: NATIVE_INPUT_MODE},
      ...customParentStorableInputs.map(({inputMode, inputLabel}) => ({
        label: inputLabel,
        value: inputMode,
      })),
    ];
    const modalContent = (
      <>
        {customParentStorableInputs.length > 0 && (
          <div className={css(styles.spacingTop)}>
            <FieldLabel label="Destination" />
            <RadioButtonSelector
              isVertical={false}
              options={radioOptions}
              onChange={this.setSelectedInputMode}
              selectedValue={this.state.selectedInputMode}
            />
          </div>
        )}
        {this.state.selectedInputMode === NATIVE_INPUT_MODE ? (
          <div className="u-small-spacing-top">
            <BaseParentStorableDataSelector
              autoFocus={true}
              canSelectMultipleGridCells={true}
              initialGridCells={[]}
              isRequired={true}
              registryId={this.props.storables[0].storableSchema.registry.id}
              selectedParent={this.state.nativeInputSelectedParentStorable}
              onLoadGrid={this.setNativeInputSelectedParent}
              onSelect={this.setNativeInputSelectedParent}
              onRemove={this.resetNativeInputSelectedParent}
              validationErrors={{}}
              // Locations are excluded because they don't count against available space
              storablesToBeMoved={this.state.selectedStorables.filter(
                (storable) => !APIUtil.isLocation(storable.id)
              )}
              // When `possibleWarning` is true, it means the SampleStatusWarning table is shown in the modal.
              // The table allows items to be unselected and re-selected, so the number of storables to be
              // moved can change.
              //
              // The location picker currently does not update appropriately when its prop
              // `storablesToBeMoved` changes. To work around this limitation, we pass this prop,
              // `numStorablesToBeMovedMayChange`, through to ParentStorableSearchableSelectTree. When it is
              // true, ParentStorableSearchableSelectTree renders ConnectedParentStorableSelectTree with a single
              // storable to be moved, instead of the true value. This makes it deprioritize only items
              // that are completely full. This way, at least the user is not prevented from choosing
              // destinations that have enough space.
              //
              // See discussion: https://benchling.slack.com/archives/C05E0Q9823D/p1699055591088919
              numStorablesToBeMovedMayChange={Boolean(possibleWarning)}
              gridCellTooltipTextByPosition={this.state.gridCellTooltipTextByPosition}
              parentStorableTypesOverride={
                selectedStorablesIncludeNonContainers
                  ? [ModelTypeConstants.LOCATION as const]
                  : ([...PARENT_STORABLE_ITEMS] as Array<StorableItemType>)
              }
            />
          </div>
        ) : (
          customParentStorableInputs
            .find(({inputMode}) => inputMode === this.state.selectedInputMode)!
            .renderCustomInput(this.state.selectedSampleIds)
        )}
        <div className={classNames('u-small-spacing-top', css(styles.labelFontSize))}>
          <HorizontalChipListField
            ids={this.state.selectedSampleIds}
            title="Selected items"
            chipsAreLinks={false}
          />
        </div>
      </>
    );

    const canProceedBasedOnSampleState = canProceedWithSamples(
      this.state.selectedSampleIds,
      this.state.shouldShowStatusWarnings,
      this.state.canProceedOnSampleStatus
    );

    // Validation error should be rendered & also block submit. On submit errors should be rendered but do not
    // block re-submitting since they could be fixed by action outside the modal
    const validationErrors = this.getValidationErrorMessages();
    let validationOrSubmitErrors: Array<string> = [];
    if (validationErrors.length) {
      validationOrSubmitErrors = validationErrors;
    } else if (this.state.submitErrorMessage) {
      validationOrSubmitErrors = [this.state.submitErrorMessage];
    }

    const banners = validationOrSubmitErrors.map((errorMessage) => ({
      bannerType: InlineBannerType.ERROR,
      text: errorMessage,
    }));
    const shouldShowErrorBanners = canProceedBasedOnSampleState && banners.length > 0;

    return (
      <Modal
        buttons={[
          {
            disabled: this.isMoveButtonDisabled(canProceedBasedOnSampleState, validationErrors),
            style: ButtonStyle.PRIMARY,
            submit: true,
            text: this.state.isSubmitting ? 'Moving...' : 'Move',
          },
        ]}
        title="Move to new location"
        onRequestClose={this.handleClose}
        onRequestSubmit={this.handleSubmit}
        expandHeight={true}
      >
        {this.state.loading ? (
          <CenteredHorizontalThrobber />
        ) : (
          <>
            {possibleWarning}
            {canProceedBasedOnSampleState && modalContent}
            {shouldShowErrorBanners && <InlineBannerStack messages={banners} />}
          </>
        )}
      </Modal>
    );
  }
}

const styles = StyleSheet.create({
  spacingTop: {
    marginTop: '10px',
  },
  labelFontSize: {fontSize: '13px'},
});
